// @ts-check
import React, { useContext } from "react"
import { severities } from "../consts/common.const"
import ToastContext from "../contexts/ToastContext.context"
import rocketIcon from "../design/assests/images/icons/rocket_icon.svg"
import checkCircle from "../design/assests/images/icons/check-circle.svg"
import lightbulb2Icon from "../design/assests/images/icons/lightbulb2.svg"

/** @type {import("./useToast.hook.types.d.ts").getIcon} */
function getIcon(icon) {
  switch (icon) {
    case "rocket":
      return rocketIcon
    case "check-circle":
      return checkCircle
    case "lightbulb2":
      return lightbulb2Icon
    default:
      return undefined
  }
}

export default function useToast() {
  const { toast, toastBR } = useContext(ToastContext)

  /** @type {import("./useToast.hook.types.d.ts").setMessage} */
  const setMessage = (
    severity = severities.error,
    headline = "Ha ocurrido un error",
    message = "",
    lifeTime = 4000,
    sticky,
    icon = null,
    iconClassName = null
  ) => {
    toast.current?.show({
      severity,
      summary: headline,
      detail: message,
      life: lifeTime,
      sticky,
      content: (
        <>
          {icon ?
            <img
              loading="eager"
              className="m-auto"
              src={getIcon(icon)}
              alt="Icon"
            />
          : <span
              className={
                iconClassName ?
                  `pi ${iconClassName}`
                : "p-toast-message-icon pi pi-times"
              }
            ></span>
          }

          <div className="p-toast-message-text">
            <span className="p-toast-summary line-height-1">{headline}</span>
            <div className="p-toast-detail">{message}</div>
          </div>
        </>
      ),
    })
  }

  /** @type {import("./useToast.hook.types.d.ts").setMessageBR} */
  const setMessageBR = ({
    severity = severities.error,
    headline = "Ha ocurrido un error",
    message = "",
    lifeTime = 4000,
    sticky,
    icon,
    iconSpanProps = {},
    ...rest
  }) => {
    toastBR.current?.show({
      summary: headline,
      detail: message,
      life: lifeTime,
      content: (
        <>
          {icon !== undefined ?
            icon
          : <span
              {...iconSpanProps}
              className={
                iconSpanProps.className ?
                  `pi ${iconSpanProps.className}`
                : "p-toast-message-icon pi pi-times"
              }
            ></span>
          }

          <div className="p-toast-message-text">
            <span className="p-toast-summary line-height-1">{headline}</span>
            <div className="p-toast-detail">{message}</div>
          </div>
        </>
      ),
      ...rest,
    })
  }

  function setSuccessMessage({
    title = "Éxito",
    message = "",
    lifeTime = undefined,
    icon = null,
    iconClassName = null,
  }) {
    setMessage(
      severities.success,
      title,
      message,
      lifeTime,
      undefined,
      icon,
      iconClassName
    )
  }

  function setErrorMessage({
    title = "Error",
    message = "",
    lifeTime = undefined,
    icon = null,
    iconClassName = null,
  }) {
    setMessage(
      severities.error,
      title,
      message,
      lifeTime,
      undefined,
      icon,
      iconClassName
    )
  }

  function setInfoMessage({
    title = "Información",
    message = "",
    lifeTime = undefined,
    icon = null,
    iconClassName = null,
  }) {
    setMessage(
      severities.info,
      title,
      message,
      lifeTime,
      undefined,
      icon,
      iconClassName
    )
  }

  function setWarnMessage({
    title = "Advertencia",
    message = "",
    lifeTime = undefined,
    sticky = undefined,
    icon = null,
    iconClassName = null,
  }) {
    setMessage(
      severities.warnig,
      title,
      message,
      lifeTime,
      sticky,
      icon,
      iconClassName
    )
  }

  return {
    setMessage,
    setSuccessMessage,
    setErrorMessage,
    setInfoMessage,
    setWarnMessage,
    setMessageBR,
  }
}
